import { useEffect, useState, useCallback } from 'react';
import FilterSearch from '../../../components/FilterSearch/FilterSearch';
import { Badge, Button, Table } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useUrlSearchParams } from 'use-url-search-params';
import { fetchNonSaleApprovalList } from './actions/action';
import DHPagination from '../../../components/Pagination';

const statusBadgeColor = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'approved':
      return 'success';
    case 'rejected':
      return 'danger';
    case 'partially_approved':
      return 'info';
    case 'pending':
    default:
      return 'warning';
  }
};

const formatStatusLabel = (status) => {
  if (!status) return '';
  return status
    .split('_')
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ');
};

const NonSaleApprovalList = ({ onChangeStatus, refreshTrigger }) => {
  const dispatch = useDispatch();
  const [paginationValue, setPaginationValue] = useUrlSearchParams({ page: 0 });
  const [limitValue, setLimitValue] = useUrlSearchParams({ limit: 10 });

  const [searchFilter, setSearchFilter] = useState({ search: '' });
  const [statusFilter, setStatusFilter] = useState({ status: 'All' });
  const [advancedFilter, setAdvancedFilter] = useState({});

  const [expandedItems, setExpandedItems] = useState({});
  const [listData, setListData] = useState({ items: [], recordsTotal: 0 });
  const [isDataLoading, setDataLoading] = useState(false);

  const { nonSaleApprovalList = {}, isLoading } = useSelector(
    (state) => state.service,
  );

  useEffect(() => {
    if (Array.isArray(nonSaleApprovalList?.items)) {
      setListData({
        items: nonSaleApprovalList.items,
        recordsTotal: nonSaleApprovalList.recordsTotal || 0,
      });
    } else {
      setListData({ items: [], recordsTotal: 0 });
    }
  }, [nonSaleApprovalList]);

  const getList = useCallback(
    async (params) => {
      try {
        setDataLoading(true);
        await fetchNonSaleApprovalList({ params, dispatch });
      } catch (error) {
        console.error('Error fetching non-sale approval list:', error);
      } finally {
        setDataLoading(false);
      }
    },
    [dispatch],
  );

  useEffect(() => {
    const delay = setTimeout(() => {
      getList({
        start: Number(paginationValue.page * limitValue.limit),
        limit: Number(limitValue.limit),
        sort: { value: 'id', order: -1 },
        search_qry: searchFilter?.search,
        product_type: statusFilter?.status,
        advancedFilter,
      });
    }, 300);
    return () => clearTimeout(delay);
  }, [
    paginationValue.page,
    limitValue.limit,
    searchFilter,
    statusFilter,
    advancedFilter,
    getList,
    refreshTrigger,
  ]);

  const toggleExpand = (key) => {
    setExpandedItems((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const items = listData.items || [];

  return (
    <>
      <FilterSearch
        setAdvancedFilter={setAdvancedFilter}
        setSearchFilter={setSearchFilter}
        setStatusFilter={setStatusFilter}
        filterType="nonSaleApproval"
      />
      <div className="dt-table">
        <div className="table-responsive">
          <Table className="OrderListTable table mb-0">
            <thead>
              <tr className="align-middle">
                <th>SI.No</th>
                <th>Service ID</th>
                <th>Customer</th>
                <th>Technician</th>
                <th>Items</th>
                <th>Total Qty</th>
                <th>Approved / Pending / Rejected</th>
                <th>Status</th>
                <th>Remarks</th>
                <th>Created At</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            {isLoading || isDataLoading ? (
              <tbody>
                {[...Array(3)].map((_, index) => (
                  <tr key={index} className="align-middle">
                    {[...Array(11)].map((__, j) => (
                      <td key={j}>
                        <p className="placeholder-glow">
                          <span className="placeholder col-6"></span>
                        </p>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            ) : items.length > 0 ? (
              items.map((row, index) => {
                const key = `${row.serviceId}-${row.technicianId}`;
                const isExpanded = !!expandedItems[key];
                return (
                  <tbody key={key}>
                    <tr className="align-middle">
                      <td>
                        {Number(paginationValue.page) *
                          Number(limitValue.limit) +
                          index +
                          1}
                      </td>
                      <td>
                        <a
                          href={`manage-service-orders/${row.serviceId}`}
                          target="_blank"
                          rel="noreferrer"
                          className="linkTag"
                        >
                          <strong>{row.serviceId}</strong>
                        </a>
                      </td>
                      <td>
                        <div>{row.customerName || '-'}</div>
                        <small className="text-muted">
                          {row.customerMobile || ''}
                        </small>
                      </td>
                      <td>
                        <div>{row.technicianName || row.technicianId}</div>
                        <small className="text-muted">
                          {row.technicianPhone || ''}
                        </small>
                      </td>
                      <td>{row.itemsCount}</td>
                      <td>{row.totalQty}</td>
                      <td>
                        <Badge color="soft-success" className="me-1">
                          A: {row.approvedCount}
                        </Badge>
                        <Badge color="soft-warning" className="me-1">
                          P: {row.pendingCount}
                        </Badge>
                        <Badge color="soft-danger">
                          R: {row.rejectedCount}
                        </Badge>
                      </td>
                      <td>
                        <Badge color={statusBadgeColor(row.overallStatus)}>
                          {formatStatusLabel(row.overallStatus)}
                        </Badge>
                      </td>
                      <td
                        title={row.remarks || ''}
                        style={{ maxWidth: 200 }}
                        className="text-truncate"
                      >
                        {row.remarks || '-'}
                      </td>
                      <td>{row.createdAt || '-'}</td>
                      <td className="text-center">
                        <Button
                          color="light"
                          size="sm"
                          onClick={() => toggleExpand(key)}
                          className="text-dark"
                        >
                          <i
                            className={`fa ${
                              isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'
                            } me-2`}
                          ></i>
                          {isExpanded ? 'Hide Items' : 'View Items'}
                        </Button>
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr>
                        <td colSpan={11} className="p-0">
                          {row.remarks && (
                            <div className="px-3 py-2 bg-light border-bottom">
                              <strong>Remarks:</strong> {row.remarks}
                            </div>
                          )}
                          <Table className="nonsale-subtable">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>SKU</th>
                                <th>Qty</th>
                                <th>Status</th>
                                <th>Approved / Rejected By</th>
                                <th>Created At</th>
                                <th>Updated At</th>
                                <th className="text-center">Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {row.items?.map((item, idx) => (
                                <tr key={item.id}>
                                  <td>{idx + 1}</td>
                                  <td>{item.sku}</td>
                                  <td>{item.qty}</td>
                                  <td>
                                    <Badge
                                      color={statusBadgeColor(item.status)}
                                    >
                                      {formatStatusLabel(item.status)}
                                    </Badge>
                                  </td>
                                  <td>{item.approvedBy || '-'}</td>
                                  <td>{item.createdAt || '-'}</td>
                                  <td>{item.updatedAt || '-'}</td>
                                  <td className="text-center">
                                    {item.status === 'pending' ? (
                                      <>
                                        <Button
                                          color="soft-success"
                                          size="sm"
                                          className="me-2"
                                          onClick={() =>
                                            onChangeStatus(item.id, 'approved')
                                          }
                                        >
                                          <i className="fa fa-check-circle me-1"></i>
                                          Approve
                                        </Button>
                                        <Button
                                          color="soft-danger"
                                          size="sm"
                                          onClick={() =>
                                            onChangeStatus(item.id, 'rejected')
                                          }
                                        >
                                          <i className="fa fa-ban me-1"></i>
                                          Reject
                                        </Button>
                                      </>
                                    ) : (
                                      <span className="text-muted">-</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </td>
                      </tr>
                    )}
                  </tbody>
                );
              })
            ) : (
              <tbody>
                <tr>
                  <td colSpan={11}>No Records found</td>
                </tr>
              </tbody>
            )}
          </Table>
        </div>

        {items.length > 0 ? (
          <DHPagination
            totalPages={listData.recordsTotal}
            currentPage={paginationValue.page}
            setPaginationValue={setPaginationValue}
            setLimitValue={setLimitValue}
          />
        ) : null}
      </div>
    </>
  );
};

export default NonSaleApprovalList;
